import { useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/button/Button";
import { Spiner } from "../../components/spinner/Spiner";
import TableList, { GridCol, GridRow, SortOrder } from "../../components/table/TableListCompanies";
import companiesQuery from "../../firestore/Companies.query";
import { Company } from "../../model/Companies";

import { CompaniesEditModal } from "./companiesEdit/CompaniesEditModal";
import Badge from '../../components/badges/Badges';
import { useDispatch } from "react-redux";
import companiesApi from "../../api/Companies.api";
import { SystemMenus } from "../../model/SystemMenus";
import { MenuSettings } from "../../model/MenuSettings";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { CheckBox } from "../../components/checkBox/CheckBox";
import { SessionKey } from "../../config/const";

// 団体管理
export const Companies = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const isDisabled = useRef(false);

  const [companiesMap, setCompaniesMap] = useState<Map<number, Company[]>>(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [isOpen, setIsOpen] = useState(false);
  const [isLode, setIsLode] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false); // 無効団体表示切替
  const [lastPage, setLastPage] = useState(1);
  const [sortKey, setSortKey] = useState<string>("displayId");
  const [sortOrder, setSortOrder] = useState<string>("ASC");
  const location = useLocation();

  // 重複呼び出し防止フラグ
  const isLoading = useRef(false);
  const isFirstLoad = useRef(true);

  // ページ取得
  const safeLoadCompaniesPage = async (
    page: number,
    showDeletedFlag: boolean,
    sortKeyParam?: string,
    sortOrderParam?: string
  ) => {
    if (isLoading.current) return;
    isLoading.current = true;
    try {
      const res = await companiesApi.requestGetCompanies(
        page,
        pageSize,
        showDeletedFlag,
        sortKeyParam || sortKey,
        sortOrderParam || sortOrder
      );
      if (res.success) {
        setSortKey(sortKeyParam ?? sortKey);
        setSortOrder(sortOrderParam ?? sortOrder);
        setCompaniesMap(prev => new Map(prev).set(page, res.companies!));
        if (res.totalCount !== undefined) {
          setTotalCount(res.totalCount);
        }
      } else {
        dispatch(alertOpen(AlertValue("団体データの取得に失敗しました", "danger")));
      }
    } catch (err) {
      console.error(err);
      dispatch(alertOpen(AlertValue("団体データの取得に失敗しました", "danger")));
    } finally {
      isLoading.current = false;
    }
  };

  // 初期表示
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const init = async () => {
      const updated = location.state?.updatedCompany;
      setIsLode(true);
      if (updated) {
        // RDBの反映待ちのため待機時間設定
        timer = setTimeout(async () => {
          try {
            const count = await companiesQuery.getCompaniesCount(false);
            setTotalCount(count);
            await safeLoadCompaniesPage(1, showDeleted);
            setCurrentPage(1);
            // location.state をクリア
            window.history.replaceState({}, document.title);
          } catch (err) {
            console.error(err);
            dispatch(alertOpen(AlertValue("団体データの取得に失敗しました", "danger")));
          } finally {
            setIsLode(false);
            isFirstLoad.current = false;
          }
        }, 600);
      } else {
        try {
          const count = await companiesQuery.getCompaniesCount(false);
          setTotalCount(count);
          await safeLoadCompaniesPage(1, showDeleted);
          setCurrentPage(1);
        } catch (err) {
          console.error(err);
          dispatch(alertOpen(AlertValue("団体データの取得に失敗しました", "danger")));
        } finally {
          setIsLode(false);
          isFirstLoad.current = false;
        }
      }
    };
    init();
    return () => clearTimeout(timer);
  }, [dispatch, pageSize, location.state, showDeleted]);

  // 表示用データ
  const prevPageRef = useRef(currentPage);

  const filteredCompanies = useMemo(() => {
    const pageToUse = companiesMap.has(currentPage) 
      ? currentPage 
      : prevPageRef.current;

    const companies = companiesMap.get(pageToUse);
    if (!companies) return [];

    return showDeleted ? companies : companies.filter(c => !c.deleteFlg);
  }, [companiesMap, currentPage, showDeleted]);

  // ページ切替
  const handlePageChange = async (page: number) => {
    // 切替前のページを ref に保存
    prevPageRef.current = currentPage;

    if (!companiesMap.has(page)) {
      await safeLoadCompaniesPage(page, showDeleted);
    }
    // データが揃ったらcurrentPageを更新
    setCurrentPage(page);
  };

  // チェックボックス切替
  const onChangeDeleteCompanies = async (e: any) => {
    const checked = e.target.checked;
    setShowDeleted(checked);
    // 切替時にキャッシュリセットして再取得
    setCompaniesMap(new Map());
    setCurrentPage(1);
    await safeLoadCompaniesPage(1, checked);
  };

  // ソート変更時
  const handleSortChange = async (key: string, order: string) => {
    setIsLode(true);
    setCompaniesMap(new Map());
    setCurrentPage(1);
    setSortKey(key);
    setSortOrder(order); 
    await safeLoadCompaniesPage(1, showDeleted, key, order);
    
    setIsLode(false);
  };

  // 団体追加タップ
  const handleOpen = () => {
    setIsOpen(true);
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const columns: GridCol[] = [
    {
      field: "displayId",
      headerName: "団体番号",
      style: "",
    },
    {
      field: "name",
      headerName: "名前",
      style: "",
    },
    {
      field: "managerName",
      headerName: "担当者",
      style: "",
    },
    {
      field: "managerEmail",
      headerName: "担当者メールアドレス",
      style: "",
    },
    {
      field: "usersCount",
      headerName: "利用者数",
      style: "text-right",
    },
    {
      field: "adminUserCount",
      headerName: "運営者数",
      style: "text-right",
    },
    {
      field: "messageCount",
      headerName: "今月の投稿数",
      style: "text-right",
    },
    {
      field: "deleteFlg",
      headerName: "利用状態",
      style: "",
    },
    {
      field: "menu",
      headerName: "",
      style: "",
      sort: false
    },
  ];

  // 今月の投稿数のElementを返却する
  function getMessageCountBadge(companyId: string, userCount: number, msgCount: number | undefined) : JSX.Element {
    // 投稿数が未設定の場合 0
    let msgCnt = 0;
    if (msgCount !== undefined) msgCnt = msgCount;

    // 契約数 * 4 の 80% が基準
    const baseCnt = (userCount * 4) * 0.8;

    if (baseCnt < msgCnt) {
      return <Badge color="stringRed">{msgCnt}</Badge>
    } else {
      return <Badge color="stringBlack">{msgCnt}</Badge>
    }
  }

  // 運営者数のElementを返却する
  function getAdminCountBadge(companyId: string, userCount: number, adminCount: number | undefined) : JSX.Element {
    // 運営者数が未設定の場合 0
    let adminCnt = 0;
    if (adminCount !== undefined) adminCnt = adminCount;

    // 契約数の 20% が基準
    const baseCnt = userCount * 0.2;

    if (baseCnt < adminCnt) {
      return <Badge color="stringRed">{adminCnt}</Badge>
    } else {
      return <Badge color="stringBlack">{adminCnt}</Badge>
    }
  }

  const onClickMenu = async (item: any) => {
    // 選択した団体IDをセッションに登録
    sessionStorage.setItem(SessionKey.SELECTED_TENANT_ID, String(item.id));
    // 利用者IDカードの権限の有無を取得
    const userIdCardAuth = await companiesApi.requestIdCardAuth(item.id);
    sessionStorage.setItem(SessionKey.USER_ID_CARD_AUTH, String(userIdCardAuth.success ? userIdCardAuth.data : false));

    navigation(`${item.id}/info`);
  }

  const rowList: GridRow[] = filteredCompanies.map((item) => ({
    rowData: {
      displayId: item.displayId,
      name: item.name,
      managerName: item.managerName,
      managerEmail: item.managerEmail,
      usersCount: <Badge color="stringBlack">{item.usersCount}</Badge>,
      adminUserCount: getAdminCountBadge(item.id, item.usersCount, item.adminUserCount),
      messageCount: getMessageCountBadge(item.id, item.usersCount, item.messageCount),
      deleteFlg: item.deleteFlg ? <Badge color="red">無効</Badge> : <Badge color="green">有効</Badge>,
      menu: (
          <Button
            onClick={() => onClickMenu(item)}
            children="詳細設定" />
        ),
    },
  }));

  const onSubmit = async (data: Company) => {
    if(!isDisabled.current){
      try {
        //新規団体登録
        const result = await companiesApi.requestCompaniesAdd(data);
        if(result.success){
          isDisabled.current = true;
          // システムメニュー登録
          const systemMenusList: SystemMenus[] = [
            {id:'', name: 'お知らせ', type: 'notice', bgColor: '#CE3797', colorName: 'pink', textColor: 'white'},
            {id:'', name: '予定表', type: 'schedule', bgColor: '#CE3797', colorName: 'pink', textColor: 'white'},
            {id:'', name: '動画', type: 'video', bgColor: '#CE3797', colorName: 'pink', textColor: 'white'}
          ];
          const resultCreateSystemMenus = await Promise.all(systemMenusList.map(async (menus) => {
            return await companiesQuery.setSystemMenus(result.data?.id, menus);
          }));
          if(resultCreateSystemMenus.length !== 3){
            throw new Error('システムメニュー作成に失敗しました。');
          }
          // メニュー初期設定
          const menuSettings: MenuSettings[] = resultCreateSystemMenus.map((systemMenu, index) => {
            return {
              id: '',
              displayOrder: (index + 1).toString(),
              enableFlag: true,
              menuType: 'system',
              menuNo: systemMenu.id
            };
          })
          const menuSettingResult = await companiesQuery.upsertMenuSettings(result.data?.id, menuSettings);
          if(menuSettingResult.length !== 3) {
            throw new Error('メニュー設定作成に失敗しました。');
          }
          // システム設定の初期データ作成
          const systemSettingResult = await companiesQuery.upsertSystemSetting(result.data?.id, {
            id: '', colorTheme: 'darkColor',
            image: ""
          });
          if(systemSettingResult.id === undefined){
            throw new Error('システム設定作成に失敗しました。');
          }
          dispatch(alertOpen(AlertValue("保存しました。", "success")));
          toggle();
          setIsLode(true);
          setCompaniesMap(new Map());
          setCurrentPage(1);
          await safeLoadCompaniesPage(1, showDeleted, sortKey, sortOrder);
          setIsLode(false);
        } else {
          throw new Error('団体データ作成に失敗しました。');
        }
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        console.error(err);
        isDisabled.current = false;
      }
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 h-full bg-[#fcfafd]">
      <CompaniesEditModal
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={true}
        children={undefined}
        onSubmit={onSubmit}
      />
      <div className="flex pt-4 pb-3 space-x-4 items-center min-w-[1160px]">
        <div className="flex-auto flex items-center">
          <h2 className="text-xl font-semibold whitespace-nowrap text-gray-900">
            団体一覧
          </h2>
          <div className={"flex w-full justify-between"}>
            <div className={"flex space-x-16 pl-16"}>
              <span className={"sm:space-x-4 text-gray-500"} style={{fontSize:12}}>
                運営者数が契約数の20%を超えた場合は赤字で表示されます<br/>
                今月の投稿数が契約数の80%を超えた場合は赤字で表示されます
              </span>
            </div>
          </div>
        </div>
        <div>
          <CheckBox
            id={"deleteFlgCompanies"}
            children={'無効の団体も表示する'}
            onChange={onChangeDeleteCompanies}
          />
        </div>
        <div>
          <Button size={"sm"} onClick={handleOpen}>団体追加</Button>
        </div>
      </div>
      {isLode === true ? <Spiner/> : (
        <>
          <TableList
            header
            footer={false}
            gridCol={columns}
            gridRow={rowList}
            limit={pageSize}
            gridRowLength={totalCount}
            externalCurrentPage={currentPage}
            setExternalCurrentPage={setCurrentPage}
            pagingFunc={handlePageChange}
            sortKey={sortKey} 
            sortOrder={sortOrder.toLowerCase() as SortOrder}
            onSortChange={handleSortChange} 
          />
        </>
      )}
    </div>
  );
};
